import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Tippy from '@tippy.js/react';
import { IconConnector, IconInfoFilled } from 'svgIcons/MotionPortalIcons';
import colors from 'theme/_colors.scss';
import { getAssetIcon } from 'helpers/icons';
import { InfoMessage } from 'sharedComponents';
import { UI_CONSTANTS } from '../../constants';
import './PowertrainAssetsConfiguration.scss';

/**
 * PowertrainAssetsConfiguration - Reusable component for displaying powertrain assets configuration
 * Used in PowertrainWizard PreviewStep and PowertrainDetails components
 *
 * @param {Array} connectedAssets - Assets that are physically connected (displayed in horizontal flow)
 * @param {Array} notConnectedAssets - Assets that are not connected (displayed in vertical list)
 * @param {string} className - Additional CSS class
 * @param {number} maxHorizontalAssets - Maximum number of assets to show in horizontal flow
 */
const PowertrainAssetsConfiguration = ({
    connectedAssets = [],
    notConnectedAssets = [],
    className = '',
    maxHorizontalAssets = 6
}) => {
    const { t: translate } = useTranslation();

    // Split connected assets for horizontal and vertical display
    const horizontalAssets = connectedAssets.slice(0, maxHorizontalAssets);
    const remainingConnectedAssets = connectedAssets.slice(maxHorizontalAssets);

    // Combine remaining connected assets with not connected assets for vertical display
    const verticalAssets = [...remainingConnectedAssets, ...notConnectedAssets];

    // Render horizontal asset item
    const renderHorizontalAssetItem = (asset, index) => {
        const hasDetails = asset.subType || asset?.outputSpeedRatio;

        return <React.Fragment key={asset.id || index}>
            <div className='asset-item-horizontal'>
                {/* Grey section with icon and name */}
                <div className='asset-header-section'>
                    <div className='asset-icon'>
                        {getAssetIcon(asset.type, UI_CONSTANTS.ICON_SIZES.EXTRA_LARGE)}
                    </div>
                    <div className='asset-name-row'>
                        <div className='asset-name' title={asset?.assetName}>
                            {asset?.assetName || translate('ABB.Powertrain.Frontend.ptToolsPowertrainConfiguration_AssetNameFallback')}
                        </div>
                        {asset.isUnmonitoredGearbox &&
                            <Tippy
                                content={translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AddGearbox_UnmonitoredAssetTooltip')}
                                interactiveBorder={10}
                                animation='scale'
                                theme='light-border'
                                trigger='click'
                                placement='top'
                                maxWidth={280}
                            >
                                <div className='unmonitored-info-icon'>
                                    <IconInfoFilled width={16} height={16} color={colors.dodgerBlue} />
                                </div>
                            </Tippy>
                        }
                    </div>
                </div>

                {/* White section with details - only render if details exist */}
                {hasDetails &&
                    <div className='asset-details-section'>
                        <div className='asset-details'>
                            <div className='asset-meta'>
                                {asset.subType &&
                                    <div className='sub-type'>
                                        {translate('ABB.Powertrain.Frontend.ptToolsPowertrainConfiguration_SubTypeLabel')}: <span className='value'>{asset.subType}</span>
                                    </div>
                                }
                                {asset?.outputSpeedRatio &&
                                    <div className='speed-ratio output-ratio'>
                                        {translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_PhysicalConnection_OutputSpeedRatioLabel')}: <span className='value'>{asset?.outputSpeedRatio}</span>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                }
            </div>
            {index < horizontalAssets.length - 1 &&
                <div className='connection-arrow'>
                    <IconConnector width={20} height={20} />
                </div>
            }
        </React.Fragment>;
    };

    // Render vertical asset item
    const renderVerticalAssetItem = (asset) =>
        <div key={asset.id || `${asset?.assetName}-${Math.random()}`} className='asset-item-vertical'>
            <div className='asset-icon'>
                {getAssetIcon(asset.type)}
            </div>
            <div className='asset-info'>
                <div className='asset-name'>
                    {asset?.assetName || translate('ABB.Powertrain.Frontend.ptToolsPowertrainConfiguration_AssetNameFallback')}
                </div>
                <div className='asset-details'>
                    <span className='asset-id'>({asset.id || asset.assetId || translate('ABB.Powertrain.Frontend.ptToolsPowertrainConfiguration_AssetIDFallback')})</span>
                </div>
            </div>
        </div>;

    return (
        <div className={`powertrain-assets-configuration ${className}`}>
            {horizontalAssets?.length > 0 ? <h4 className='horizontal-assets-section-title'>{translate('ABB.Powertrain.Frontend.ptToolsPowertrainConfiguration_AssetConfiguration_PhysicalConnectionLabel')}</h4> : null}

            {/* Horizontal Assets Flow - Connected Assets */}
            {horizontalAssets?.length > 0 ?
                <div className='horizontal-assets-container'>
                    <div className='assets-flow'>
                        {horizontalAssets.map((asset, index) => renderHorizontalAssetItem(asset, index))}
                    </div>
                </div>
                : <div className={`powertrain-assets-configuration ${className}`}>
                    <h4 className='horizontal-assets-section-title'>{translate('ABB.Powertrain.Frontend.ptToolsPowertrainConfiguration_AssetConfiguration_PhysicalConnectionLabel')}</h4>
                    <InfoMessage title='ABB.Powertrain.Frontend.ptToolsPowertrainConfigurationTab_Details_No_Physical_Connection_AssetsAssigned_label' />
                </div>}
            {verticalAssets?.length > 0 && <div className='assets-divider'></div>}
            {verticalAssets?.length > 0 && <h4 className='vertical-assets-section-title'>{translate('ABB.Powertrain.Frontend.ptToolsPowertrainConfiguration_AssetConfiguration_NotConnectedAssetsLabel')}</h4>}

            {/* Vertical Assets List - Remaining connected + Not connected */}
            {verticalAssets?.length > 0 &&
                <div className='vertical-assets-container'>
                    <div className='assets-list'>
                        {/* Remaining connected assets */}
                        {remainingConnectedAssets.map(asset => renderVerticalAssetItem(asset))}

                        {/* Not connected assets */}
                        {notConnectedAssets.map(asset => renderVerticalAssetItem(asset))}
                    </div>
                </div>
            }
        </div>
    );
};

PowertrainAssetsConfiguration.propTypes = {
    connectedAssets: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        type: PropTypes.string,
        subType: PropTypes.string,
        inputSpeedRatio: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        outputSpeedRatio: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        assetId: PropTypes.string,
        isUnmonitoredGearbox: PropTypes.bool
    })),
    notConnectedAssets: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        type: PropTypes.string,
        subType: PropTypes.string,
        inputSpeedRatio: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        outputSpeedRatio: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        assetId: PropTypes.string
    })),
    className: PropTypes.string,
    maxHorizontalAssets: PropTypes.number
};

export default PowertrainAssetsConfiguration;
